import store from "redux/store";
import axios from "axios";

// classes
import Aircraft from "classes/aircraft";
import Statistics from "./statistics";
import Route from "classes/route";
import Flight from "./flight";

// utilities
import { getAirportById } from "hooks/useAirport";
import { getCurrentDemand } from "hooks/useDemand";

// data
import seats from "assets/json/seats.json";
import fuelSuppliers from "assets/json/fuelSuppliers.json";
import cateringSuppliers from "assets/json/cateringSuppliers.json";

// api
import api from "api/api";

class Revenue {
	constructor(aircraftIdent, routeId, displayRevenue = true) {
		this.aircraft = Aircraft.get(aircraftIdent);

		const route = Route.get(routeId);
		this.route = route;
		this.routeDistance = route.distance;

		const autoPrice = Revenue.generatePrices(this.routeDistance);
		this.prices = Flight.get(this.aircraft.currentFlight).prices || Revenue.generatePrices(this.routeDistance);

		this.reputation = 0.5;
		this.comfort = this.getGenericComfort();
		this.classComfort = this.getClassComfort();

		const aircraftCapacity = Object.values(this.aircraft.specs.capacity);

		this.maxPassengers = aircraftCapacity.reduce((total, current) => total + current, 0);
		this.passengers = aircraftCapacity.map((seats, i) => {
			let costFactor = this.prices[i] / autoPrice[i];
			this.prices[i] < autoPrice[i] && (costFactor = costFactor / 2 + 1);

			let passengersCount = ((this.reputation + this.comfort) / 2) * seats * (this.classComfort[i] / 100);

			this.prices[i] < autoPrice[i] ? (passengersCount *= costFactor) : (passengersCount /= costFactor);
			passengersCount = Math.round(passengersCount);
			passengersCount > seats && (passengersCount = seats);
			passengersCount < 0 && (passengersCount = 0);

			let demand = getCurrentDemand(route.from, route.to);
			if (demand == null) {
				console.error(`Couldn't find demand data for route ${route.from} -> ${route.to}.`);
				demand = [Infinity, Infinity, Infinity, Infinity];
			} else {
				demand = [demand.demand_economy, demand.demand_premium, demand.demand_business, demand.demand_first];
			}

			passengersCount > demand[i] && (passengersCount = demand[i]);
			return passengersCount;
		});

		this.ticketEarnings = aircraftCapacity.reduce((total, x, i) => total + this.passengers[i] * this.prices[i], 0);
		this.dutyFreeEarnings = this.getDutyFreeEarnings();
		this.cateringEarnings = this.getCateringEarnings();

		this.fixedFees = this.getFixedFees();
		this.fuelCost = this.getFuelCost();
		this.total = Math.round(this.ticketEarnings - this.fuelCost - this.fixedFees + this.dutyFreeEarnings);

		displayRevenue && this.showInPopup();
		displayRevenue && Statistics.addPassengersTravelled(this.passengers);
		this.updateDemand();
	}

	showInPopup() {
		store.dispatch({ type: "SET_REVENUE_POPUP_DATA", payload: this });
		store.dispatch({ type: "REVENUE_POPUP_OPEN" });
	}

	/**
	 * A function that gets the total amount of fixed fees.
	 * @returns {Number} Total fixed fee.
	 */
	getFixedFees() {
		return Math.round(this.routeDistance * 0.3);
	}

	/**
	 * A function that calculates the total fuel cost for a given route and aircraft.
	 * @return {Number} Fuel price.
	 */
	getFuelCost() {
		const fuelSupplierId = store.getState().groupInfo.fuelSupplier;
		const fuelSupplier = fuelSuppliers[fuelSupplierId];
		const fuelPrices = Object.values(store.getState().fuelPrices[fuelSupplierId].prices);
		let price = fuelPrices[fuelPrices.length - 1]; // Per 1000 gallons

		const { iso_country: country } = getAirportById(store.getState().airports, this.aircraft.departedFrom);
		if (!fuelSupplier.availability.includes(country)) {
			price *= fuelSupplier.extraFee / 100 + 1;
		}

		this.fuelAmount = Math.round(this.aircraft.specs.fuel_consumption * this.routeDistance); // In pounds

		return Math.round((this.fuelAmount / 1000) * price);
	}

	/**
	 * A method that calculates the overall comfort (cleaning, duty free, etc.).
	 * @return {Number} Array of comfort % in each class.
	 */
	getGenericComfort() {
		const baseComfort = 0.5;
		const settings = store.getState().airlineInfo.settings;
		let comfort = baseComfort;

		settings.onlineCheckIn && (comfort += 0.1);
		settings.dutyFree && (comfort += 0.1);
		settings.cleaning > 0 && (comfort += (21 - settings.cleaning) * 0.015);

		return comfort;
	}

	/**
	 * A method that calculates the comfort in each class.
	 * @return {Array[Number]} Array of comfort % in each class.
	 */
	getClassComfort() {
		const { seats: seatIds, ife, cameras, wifi } = this.aircraft.specs;
		const { tailCamera, wheelCamera } = cameras;
		const allSeats = Object.values(seats).flat();

		const max = [16.5, 21.5, 29, 34];
		const aircraftComfortArray = seatIds.map((seatId, i) => {
			const seatComfort = allSeats.find(s => s.id === seatId).comfort * 5;
			const ifeComfort = ife[i] * 3;
			const camerasComfort = (tailCamera + wheelCamera) * ife[i]; // if no ife no bonus for cameras
			const wifiComfort = wifi * 2;
			const total = seatComfort + ifeComfort + camerasComfort + wifiComfort;

			return Math.round((total / max[i]) * 100);
		});

		const { economy, premium, business, first } = store.getState().airlineInfo.settings;
		const amenitiesComfortArray = seatIds.map((_v, i) => {
			if (i === 0) {
				economy.blanketAndPillow ??= 6;
				const blanketAndPillow = Math.round(Math.abs(25 - (economy.blanketAndPillow - 2)) * (85 / 24));
				const wiFi = economy.freeWiFi * 0.15;

				return blanketAndPillow + wiFi;
			}

			if (i === 1) {
				const checkIn = premium.priorityCheckIn * 0.3;
				const boarding = premium.priorityBoarding * 0.3;
				const wiFi = premium.freeWiFi * 0.15;

				return checkIn + boarding + wiFi;
			}

			if (i === 2) {
				const checkIn = business.priorityCheckIn * 0.3;
				const boarding = business.priorityBoarding * 0.5;
				const wiFi = economy.freeWiFi * 0.2;

				return checkIn + boarding + wiFi;
			}

			if (i === 3) {
				const checkIn = first.priorityCheckIn * 0.2;
				const boarding = first.priorityBoarding * 0.5;
				const wiFi = first.freeWiFi * 0.3;

				return checkIn + boarding + wiFi;
			}

			return 0;
		});

		return aircraftComfortArray.map((acc, i) => (acc + amenitiesComfortArray[i]) / 2);
	}

	getDutyFreeEarnings() {
		const hasDutyFree = store.getState().airlineInfo.settings.dutyFree;
		if (!hasDutyFree) return 0;

		const allPassengers = this.passengers.reduce((acc, v) => acc + v, 0);
		const percent = +(Math.random() * (0.05 - 0.3) + 0.3).toFixed(2);
		const passengersThatBought = Math.round(allPassengers * percent);
		let earnings = 0;

		for (let i = 0; i < passengersThatBought; i++) {
			earnings += Math.random() * (5 - 50) + 50;
		}

		return Math.round(earnings);
	}

	getCateringEarnings() {
		let { supplierId, settings } = store.getState().catering;
		if (!supplierId) return 0;

		const supplier = cateringSuppliers.find(({ id }) => id === +supplierId);
		const departureAirport = getAirportById(store.getState().airports, this.aircraft.departedFrom);
		const departureCountry = departureAirport.iso_country;

		if (!supplier.availability.includes(departureCountry)) return 0;
		const flightLength = this.routeDistance / this.aircraft.specs.speed[0];
		const hasAlcohol = store.getState().airlineInfo.settings.alcohol;
		settings = Object.values(settings);

		const earnings = this.passengers.map((count, i) => {
			let earning = 0;

			if (count <= 0) return 0;
			Object.entries(settings[i]).forEach(([key, value], i) => {
				if (key === "alcohol" && !hasAlcohol) return true;

				// free so everyone takes
				if (flightLength >= value.flightLength) {
					earning -= supplier.prices[key] * count;
					return true;
				}

				const minPercent = flightLength / (i + 1);
				const maxPercent = (i + 1) / 10;
				let percent = Math.random() * (maxPercent - minPercent) + minPercent;

				if (flightLength <= 4 && percent * count >= 20) {
					percent -= 0.15;
				}

				earning += supplier.prices[key] * value.sellPriceMultiplier * (count * percent);
			});

			return earning;
		});

		return Math.round(earnings.reduce((acc, v) => acc + v, 0));
	}

	async updateDemand() {
		store.dispatch({
			type: "SUBTRACT_DEMAND",
			payload: { amount: this.passengers, from: this.route.from, to: this.route.to }
		});

		try {
			await axios.patch(`${api}/group/demand?origin=${this.route.from}&destination=${this.route.to}`, {
				demand: this.passengers
			});
		} catch (err) {
			console.error(err);
		}
	}

	static getTotal(revenues) {
		const initial = {
			total: 0,
			ticketEarnings: 0,
			passengers: [0, 0, 0, 0],
			fuelAmount: 0,
			fuelCost: 0,
			fixedFees: 0,
			dutyFreeEarnings: 0,
			cateringEarnings: 0
		};

		const total = revenues.reduce((total, r) => {
			if (r == null) return total;

			Object.entries(r).reduce((subtotal, [key, value]) => {
				if (subtotal[key] == null) return subtotal;
				if (Array.isArray(value)) {
					subtotal[key] = subtotal[key].map((a, i) => a + value[i]);
					return subtotal;
				}

				subtotal[key] = +subtotal[key] + value;
				return subtotal;
			}, total);

			return total;
		}, initial);

		total.aircraftDeparted = revenues.length;

		store.dispatch({ type: "SET_REVENUE_POPUP_DATA", payload: total });
		store.dispatch({ type: "REVENUE_POPUP_OPEN" });
		return total;
	}

	/**
	 * A static method to generate ticket prices based on the flight distance.
	 * @param {Number} routeDistance The flight distance in km.
	 * @return {Array<Number>} The prices in each class.
	 */
	static generatePrices(routeDistance) {
		const economyPrice = Math.round(routeDistance < 10000 ? 0.4 * routeDistance : 0.3 * routeDistance);
		const premiumPrice = Math.round(routeDistance < 7000 ? 0.5 * routeDistance : 0.42 * routeDistance);
		const businessPrice = Math.round(routeDistance < 5000 ? 0.65 * routeDistance : 0.45 * routeDistance);
		const firstPrice = Math.round(routeDistance < 7000 ? 0.8 * routeDistance : 0.95 * routeDistance);
		return [economyPrice, premiumPrice, businessPrice, firstPrice];
	}
}

export default Revenue;
